import React, { useState, useEffect, useContext } from 'react';
import { FirebaseContext, AuthContext } from './GameWrapper';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';

const Leaderboard = ({ show, onClose }) => {
  const { db } = useContext(FirebaseContext);
  const { user, userProgress } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('all-time');
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState(null);
  const [periodScore, setPeriodScore] = useState(0); // Track period-specific score

  // Format display name to hide email and format names properly
  const formatDisplayName = (name) => {
    if (!name) return 'Anonymous Player';
    
    // If it looks like an email, don't display it
    if (name.includes('@')) return 'Player';
    
    // If name has a space (first and last name), format as NameS
    if (name.includes(' ')) {
      const nameParts = name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      return `${firstName}${lastName.charAt(0)}`;
    }
    
    return name;
  };

  // Get ISO week format for a date
  const getWeekId = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 4).getTime()) / 86400000 / 7) + 1;
    return `${d.getFullYear()}-W${week.toString().padStart(2, '0')}`;
  };

  const fetchLeaderboard = async (tabType) => {
    setLoading(true);
    try {
      if (tabType === 'daily' || tabType === 'weekly') {
        // For daily and weekly, use the leaderboard documents
        let docId;
        let periodKey;
        
        if (tabType === 'daily') {
          const today = new Date().toISOString().split('T')[0];
          docId = `daily_${today}`;
          periodKey = today;
          console.log(`Fetching daily leaderboard for ${today}, docId: ${docId}`);
        } else { // weekly
          const now = new Date();
          const weekId = getWeekId(now);
          docId = `weekly_${weekId}`;
          periodKey = weekId;
          console.log(`Fetching weekly leaderboard for ${weekId}, docId: ${docId}`);
        }
        
        const leaderboardRef = doc(db, 'Leaderboards', docId);
        console.log(`Attempting to fetch document: ${docId}`);
        const leaderboardDoc = await getDoc(leaderboardRef);
        
        if (leaderboardDoc.exists()) {
          console.log(`${tabType.charAt(0).toUpperCase() + tabType.slice(1)} leaderboard document exists`);
          const data = leaderboardDoc.data();
          console.log(`Leaderboard data:`, data);
          
          // Add rank to each leader and limit to top 10
          const leaders = (data.leaders || [])
            .slice(0, 10) // Limit to top 10
            .map((leader, index) => ({
              ...leader,
              rank: index + 1
            }));
          
          setLeaderboardData(leaders);
          console.log(`Setting leaderboard data with ${leaders.length} entries`);
          
          // Find user's rank and period score if they're logged in
          if (user) {
            const userEntry = data.leaders.find(leader => leader.userId === user.uid);
            if (userEntry) {
              console.log(`User found in leaderboard with score: ${userEntry.score}`);
              const userIndex = data.leaders.findIndex(leader => leader.userId === user.uid);
              setUserRank(userIndex + 1);
              setPeriodScore(userEntry.score); // Set period-specific score
            } else {
              console.log(`User not found in ${tabType} leaderboard`);
              setUserRank(null);
              
              // Check user's period score from their profile
              if (userProgress) {
                if (tabType === 'daily' && userProgress.dailyCompletions) {
                  setPeriodScore(userProgress.dailyCompletions[periodKey] || 0);
                } else if (tabType === 'weekly' && userProgress.weeklyCompletions) {
                  setPeriodScore(userProgress.weeklyCompletions[periodKey] || 0);
                } else {
                  setPeriodScore(0);
                }
              } else {
                setPeriodScore(0);
              }
            }
          }
        } else {
          // No leaderboard exists yet for this timeframe
          console.log(`No ${tabType} leaderboard exists for today/this week`);
          setLeaderboardData([]);
          setUserRank(null);
          
          // Check user's period score from their profile
          if (user && userProgress) {
            if (tabType === 'daily' && userProgress.dailyCompletions) {
              setPeriodScore(userProgress.dailyCompletions[periodKey] || 0);
            } else if (tabType === 'weekly' && userProgress.weeklyCompletions) {
              setPeriodScore(userProgress.weeklyCompletions[periodKey] || 0);
            } else {
              setPeriodScore(0);
            }
          } else {
            setPeriodScore(0);
          }
        }
      } else {
        // For all-time, query all users directly
        console.log("Querying all users for all-time leaderboard...");
        
        const usersRef = collection(db, 'users');
        const q = query(
          usersRef, 
          orderBy('gamesCompleted', 'desc'), 
          limit(10) // Limit to top 10 users
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          console.log(`Found ${querySnapshot.size} users for all-time leaderboard`);
          
          // Create leaders from user data
          const leaders = querySnapshot.docs
            .map((doc, index) => {
              const userData = doc.data();
              return {
                userId: doc.id,
                displayName: formatDisplayName(userData.displayName),
                score: userData.gamesCompleted || 0,
                rank: index + 1
              };
            })
            .filter(leader => leader.score > 0); // Only include users with completed games
          
          console.log(`Final all-time leaderboard has ${leaders.length} users`);
          setLeaderboardData(leaders);
          
          // Find user's rank if they're logged in
          if (user) {
            // For all-time, we need to find the user's rank among all users
            // This could be outside the top 10, so we need a separate query
            if (leaders.some(leader => leader.userId === user.uid)) {
              // User is in the top 10, use the index
              const userIndex = leaders.findIndex(leader => leader.userId === user.uid);
              const userScore = leaders[userIndex].score;
              console.log(`User found in top 10 at position ${userIndex + 1} with score ${userScore}`);
              setUserRank(userIndex + 1);
              setPeriodScore(userScore); // For all-time, this is the total games completed
            } else {
              // User is not in top 10, make a separate query to find their rank
              try {
                console.log("User not in top 10, searching for their rank...");
                const betterQuery = query(
                  usersRef,
                  orderBy('gamesCompleted', 'desc')
                );
                
                const fullSnapshot = await getDocs(betterQuery);
                const userRankIndex = fullSnapshot.docs.findIndex(doc => doc.id === user.uid);
                
                if (userRankIndex !== -1) {
                  const userData = fullSnapshot.docs[userRankIndex].data();
                  console.log(`User found at overall position ${userRankIndex + 1} with score ${userData.gamesCompleted || 0}`);
                  setUserRank(userRankIndex + 1);
                  setPeriodScore(userData.gamesCompleted || 0); // For all-time, this is the total games completed
                } else {
                  console.log("User not found in any ranking");
                  setUserRank(null);
                  setPeriodScore(userProgress?.gamesCompleted || 0);
                }
              } catch (rankError) {
                console.error("Error finding user rank:", rankError);
                setUserRank(null);
                setPeriodScore(userProgress?.gamesCompleted || 0);
              }
            }
          } else {
            console.log("No user logged in, not setting user rank");
            setUserRank(null);
            setPeriodScore(0);
          }
        } else {
          console.log("No users found for all-time leaderboard");
          setLeaderboardData([]);
          setUserRank(null);
          setPeriodScore(userProgress?.gamesCompleted || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLeaderboardData([]);
      setPeriodScore(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchLeaderboard(activeTab);
    }
  }, [show, activeTab, db]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (!show) return null;

  // Get the appropriate period label based on active tab
  const getPeriodLabel = () => {
    switch (activeTab) {
      case 'daily':
        return 'today';
      case 'weekly':
        return 'this week';
      default:
        return 'all time';
    }
  };

  return (
    <div className="connections-leaderboard-wrapper">
      <div className="modal-overlay">
        <div className="modal leaderboard-modal">
          <div className="modal-header">
            <h2>Leaderboard</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="modal-content">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === 'daily' ? 'active' : ''}`} 
                onClick={() => handleTabChange('daily')}
              >
                Today
              </button>
              <button 
                className={`tab ${activeTab === 'weekly' ? 'active' : ''}`} 
                onClick={() => handleTabChange('weekly')}
              >
                Weekly
              </button>
              <button 
                className={`tab ${activeTab === 'all-time' ? 'active' : ''}`} 
                onClick={() => handleTabChange('all-time')}
              >
                All Time
              </button>
            </div>
            
            {loading ? (
              <div className="loading">Loading leaderboard data...</div>
            ) : (
              <>
                <div className="leaderboard-container">
                  <div className="leaderboard-header">
                    <span className="rank-col">Rank</span>
                    <span className="player-col">Player</span>
                    <span className="stats-col">Completed</span>
                  </div>
                  
                  <div className="leaderboard-list">
                    {leaderboardData.length > 0 ? (
                      leaderboardData.map((player) => (
                        <div 
                          key={player.userId} 
                          className={`leaderboard-item ${player.userId === user?.uid ? 'current-user' : ''}`}
                        >
                          <span className="rank-col">
                            {player.rank <= 3 ? (
                              <span className={`trophy trophy-${player.rank}`}>
                                {player.rank === 1 ? '🏆' : player.rank === 2 ? '🥈' : '🥉'}
                              </span>
                            ) : player.rank}
                          </span>
                          <span className="player-col">
                            {player.displayName || 'Anonymous Player'}
                          </span>
                          <span className="stats-col">{player.score || 0}</span>
                        </div>
                      ))
                    ) : (
                      <div className="no-data">No leaderboard data available for this time period.</div>
                    )}
                  </div>
                </div>
                
                {user ? (
                  userRank === null ? (
                    <div className="user-prompt">
                      <p>You're not on the leaderboard yet! Complete more games to earn your rank.</p>
                      {periodScore > 0 && (
                        <div className="user-stats">
                          <p>Games completed {getPeriodLabel()}: {periodScore}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="user-stats">
                      <p>Your rank: #{userRank} • Games completed {getPeriodLabel()}: {periodScore}</p>
                    </div>
                  )
                ) : (
                  <div className="login-prompt">
                    <p>Sign in to track your stats and appear on the leaderboard!</p>
                    <button className="login-btn" onClick={() => {
                      onClose();
                      // Trigger login modal
                      const loginBtn = document.getElementById('login-button');
                      if (loginBtn) loginBtn.click();
                    }}>
                      Sign In / Sign Up
                    </button>
                  </div>
                )}
              </>
            )}
            
            <div className="leaderboard-footer">
              <p>Keep playing to improve your rank and earn bragging rights!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;